import _defineProperty from "/opt/findep/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/findep/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
const _excluded = ["onSuccess", "onError", "useOneTap", "promptMomentNotification", "type", "theme", "size", "text", "shape", "logo_alignment", "width", "locale"],
  _excluded2 = ["flow", "scope", "onSuccess", "onError"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState, useRef, useEffect, createContext, useMemo, useContext, useCallback } from 'react';
function useLoadGsiScript() {
  let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    onScriptLoadSuccess,
    onScriptLoadError
  } = options;
  const [scriptLoadedSuccessfully, setScriptLoadedSuccessfully] = useState(false);
  const onScriptLoadSuccessRef = useRef(onScriptLoadSuccess);
  onScriptLoadSuccessRef.current = onScriptLoadSuccess;
  const onScriptLoadErrorRef = useRef(onScriptLoadError);
  onScriptLoadErrorRef.current = onScriptLoadError;
  useEffect(() => {
    const scriptTag = document.createElement('script');
    scriptTag.src = 'https://accounts.google.com/gsi/client';
    scriptTag.async = true;
    scriptTag.defer = true;
    scriptTag.onload = () => {
      var _a;
      setScriptLoadedSuccessfully(true);
      (_a = onScriptLoadSuccessRef.current) === null || _a === void 0 ? void 0 : _a.call(onScriptLoadSuccessRef);
    };
    scriptTag.onerror = () => {
      var _a;
      setScriptLoadedSuccessfully(false);
      (_a = onScriptLoadErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onScriptLoadErrorRef);
    };
    document.body.appendChild(scriptTag);
    return () => {
      document.body.removeChild(scriptTag);
    };
  }, []);
  return scriptLoadedSuccessfully;
}
const GoogleOAuthContext = createContext(null);
function GoogleOAuthProvider(_ref) {
  let {
    clientId,
    onScriptLoadSuccess,
    onScriptLoadError,
    children
  } = _ref;
  const scriptLoadedSuccessfully = useLoadGsiScript({
    onScriptLoadSuccess,
    onScriptLoadError
  });
  const contextValue = useMemo(() => ({
    clientId,
    scriptLoadedSuccessfully
  }), [clientId, scriptLoadedSuccessfully]);
  return React.createElement(GoogleOAuthContext.Provider, {
    value: contextValue
  }, children);
}
function useGoogleOAuth() {
  const context = useContext(GoogleOAuthContext);
  if (!context) {
    throw new Error('Google OAuth components must be used within GoogleOAuthProvider');
  }
  return context;
}
const containerHeightMap = {
  large: 40,
  medium: 32,
  small: 20
};
function GoogleLogin(_ref2) {
  let {
      onSuccess,
      onError,
      useOneTap,
      promptMomentNotification,
      type = 'standard',
      theme = 'outline',
      size = 'large',
      text,
      shape,
      logo_alignment,
      width,
      locale
    } = _ref2,
    props = _objectWithoutProperties(_ref2, _excluded);
  const btnContainerRef = useRef(null);
  const {
    clientId,
    scriptLoadedSuccessfully
  } = useGoogleOAuth();
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const promptMomentNotificationRef = useRef(promptMomentNotification);
  promptMomentNotificationRef.current = promptMomentNotification;
  useEffect(() => {
    var _a, _b, _c;
    if (!scriptLoadedSuccessfully) return;
    (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.id.initialize(_objectSpread({
      client_id: clientId,
      callback: credentialResponse => {
        var _a;
        if (!credentialResponse.clientId || !credentialResponse.credential) {
          return (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef);
        }
        onSuccessRef.current(credentialResponse);
      }
    }, props));
    (_b = window.google) === null || _b === void 0 ? void 0 : _b.accounts.id.renderButton(btnContainerRef.current, {
      type,
      theme,
      size,
      text,
      shape,
      logo_alignment,
      width,
      locale
    });
    if (useOneTap) (_c = window.google) === null || _c === void 0 ? void 0 : _c.accounts.id.prompt(promptMomentNotificationRef.current);
    return () => {
      var _a;
      if (useOneTap) (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.id.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, scriptLoadedSuccessfully, useOneTap, type, theme, size, text, shape, logo_alignment, width, locale]);
  return React.createElement("div", {
    ref: btnContainerRef,
    style: {
      display: 'inline-flex',
      height: containerHeightMap[size]
    }
  });
}
function googleLogout() {
  var _a;
  (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.id.disableAutoSelect();
}

/* eslint-disable import/export */
function useGoogleLogin(_ref3) {
  let {
      flow = 'implicit',
      scope = '',
      onSuccess,
      onError
    } = _ref3,
    props = _objectWithoutProperties(_ref3, _excluded2);
  const {
    clientId,
    scriptLoadedSuccessfully
  } = useGoogleOAuth();
  const clientRef = useRef();
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  useEffect(() => {
    var _a;
    if (!scriptLoadedSuccessfully) return;
    const clientMethod = flow === 'implicit' ? 'initTokenClient' : 'initCodeClient';
    const client = (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.oauth2[clientMethod](_objectSpread({
      client_id: clientId,
      scope: "openid profile email ".concat(scope),
      callback: response => {
        var _a, _b;
        if (response.error) return (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef, response);
        (_b = onSuccessRef.current) === null || _b === void 0 ? void 0 : _b.call(onSuccessRef, response);
      }
    }, props));
    clientRef.current = client;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, scriptLoadedSuccessfully, flow, scope]);
  const loginImplicitFlow = useCallback(overrideConfig => clientRef.current.requestAccessToken(overrideConfig), []);
  const loginAuthCodeFlow = useCallback(() => clientRef.current.requestCode(), []);
  return flow === 'implicit' ? loginImplicitFlow : loginAuthCodeFlow;
}
function useGoogleOneTapLogin(_ref4) {
  let {
    onSuccess,
    onError,
    promptMomentNotification,
    cancel_on_tap_outside,
    hosted_domain
  } = _ref4;
  const {
    clientId,
    scriptLoadedSuccessfully
  } = useGoogleOAuth();
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const promptMomentNotificationRef = useRef(promptMomentNotification);
  promptMomentNotificationRef.current = promptMomentNotification;
  useEffect(() => {
    var _a, _b;
    if (!scriptLoadedSuccessfully) return;
    (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.id.initialize({
      client_id: clientId,
      callback: credentialResponse => {
        var _a;
        if (!credentialResponse.clientId || !credentialResponse.credential) {
          return (_a = onErrorRef.current) === null || _a === void 0 ? void 0 : _a.call(onErrorRef);
        }
        onSuccessRef.current(credentialResponse);
      },
      hosted_domain,
      cancel_on_tap_outside
    });
    (_b = window.google) === null || _b === void 0 ? void 0 : _b.accounts.id.prompt(promptMomentNotificationRef.current);
    return () => {
      var _a;
      (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.id.cancel();
    };
  }, [clientId, scriptLoadedSuccessfully, cancel_on_tap_outside, hosted_domain]);
}

/**
 * Checks if the user granted all the specified scope or scopes
 * @returns True if all the scopes are granted
 */
function hasGrantedAllScopesGoogle(tokenResponse, firstScope) {
  if (!window.google) return false;
  for (var _len = arguments.length, restScopes = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
    restScopes[_key - 2] = arguments[_key];
  }
  return window.google.accounts.oauth2.hasGrantedAllScopes(tokenResponse, firstScope, ...restScopes);
}

/**
 * Checks if the user granted any of the specified scope or scopes.
 * @returns True if any of the scopes are granted
 */
function hasGrantedAnyScopeGoogle(tokenResponse, firstScope) {
  if (!window.google) return false;
  for (var _len2 = arguments.length, restScopes = new Array(_len2 > 2 ? _len2 - 2 : 0), _key2 = 2; _key2 < _len2; _key2++) {
    restScopes[_key2 - 2] = arguments[_key2];
  }
  return window.google.accounts.oauth2.hasGrantedAnyScope(tokenResponse, firstScope, ...restScopes);
}
export { GoogleLogin, GoogleOAuthProvider, googleLogout, hasGrantedAllScopesGoogle, hasGrantedAnyScopeGoogle, useGoogleLogin, useGoogleOneTapLogin };